<template>
  <div class="item-container">
    <error-dialog ref="errorDialog" @onUserConfirmation="dataTransmissionError()"/>
    <!-- itemId as key for refreshing component + timer -->
    <timer
      class="interaction-timer"
      :key="'timer-' + item.itemId"
      v-if="item.timeLimit && !item.postponed"
      :timeLimit="item.timeLimit"
      :currentTimeLeft="currentItemTimeLimit"
      @timesUp="handleTimesUp"
      :paused="timerPaused"
    />
    <component
      class="h-100"
      :key="item.itemId"
      :is="currentComponent"
      v-bind="currentComponentProps"
      @answer="setAnswer"
      @ctrlButtonHidden="handleCtrlButtonHidden"
    />
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { hasMetaData, isDone } from '@/utils/items'
import { ITEMS } from '@/constants/items'
import errorDialog from '@/components/ui/errorDialog'
import ItemsContainerMixin from './mixins/ItemsContainerMixin'
import timer from '@/components/ui/timer'
import { QUERY_PARAM_IFRAME } from '@/constants/misc'

export default {
  name: 'ItemContainer',
  components: {
    timer,
    'error-dialog': errorDialog,
  },
  mixins: [ItemsContainerMixin],
  props: {
    item: {
      type: Object
    },
    interactionInstanceToken: {
      type: String
    },
    nextClicked: {
      type: Boolean
    }
  },
  watch: {
    item() {
      if(!this.item.opened) {
        this.openCurrentItem()
      }
      if (hasMetaData(this.item)) {
        this.getItemMetaData()
      }
      this.timerPaused = false;

      window.scrollTo(0,0);

      if(isDone(this.item)) {
        this.notifyDone();
      }
    },
    nextClicked(newVal) {
      if (newVal) {
        this.handleNext()
      }
    }
  },
  data() {
    return {
      currentAnswer: null,
      timerKey: 0,
      timerPaused: false,
    }
  },
  computed: {
    ...mapGetters([
      'instanceToken',
      'colorVariant',
      'currentItemNumber',
      'currentItemMetaData',
      'requestError',
      'locale',
      'interactionInstance'
    ]),
    currentComponent() {
      return this.item && ITEMS[this.item.itemType]
    },
    currentComponentProps() {
      return {
        colorVariant: this.colorVariant,
        item: this.item,
        metaData: this.currentItemMetaData,
        interactionInstanceToken: this.instanceToken,
        locale: this.locale,
        itemProperties: this.itemProperties,
        externalUserReference: this.interactionInstance.externalRef,
        organizationType: this.interactionInstance.organizationType,
        productLevel: this.interactionInstance.productLevel,
        productType: this.interactionInstance.productType,
        clientToken: this.interactionInstance.clientToken
      }
    },
    currentItemTimeLimit() {
      const { timeLimit, opened } = this.item;
      if (timeLimit && opened) {
        const currentTimeLimit = timeLimit + opened - new Date().getTime();

        if (currentTimeLimit >= 0) {
          return currentTimeLimit;
        }
        return 0;
      }

      return timeLimit
    }
  },
  methods: {
    ...mapActions([
      'openItem',
      'answerItem',
      'setCurrentItemNumber',
      'fetchItemMetaData',
      'postponeItem',
      'showDialog'
    ]),
    handleCtrlButtonHidden(value) {
      this.$emit('ctrlButtonHidden', value)
    },
    openCurrentItem() {
      const interactionInstanceToken = this.instanceToken
      const itemId = this.item.itemId
      const timeStamp = new Date().getTime()

      this.openItem({ interactionInstanceToken, itemId, timeStamp })
    },
    getItemMetaData() {
      this.fetchItemMetaData({
        interactionInstanceToken: this.instanceToken,
        itemId: this.item.itemId
      })
    },
    setAnswer(result) {
      if (result) {
        this.currentAnswer = result
        this.$emit('answer', true)
      } else {
        this.$emit('answer', false)
      }
    },
    async handleNext() {
      this.timerPaused = true

      await this.answerItem({
        interactionInstanceToken: this.instanceToken,
        itemId: this.item.itemId,
        answer: this.currentAnswer
      });

      if (this.requestError === null){
        this.setCurrentItemNumber({ number: this.currentItemNumber + 1 });
        this.$emit('answered');
      } else {
        this.$refs.errorDialog.show();
      }
    },
    dataTransmissionError() {
      this.$emit('resetAfterDataTransmissionError');
      this.timerPaused = false
    },
    handleTimesUp() {
      this.showDialog({
        modal: true,
        title: this.$t('INTERACTION_BASE_TIMESUP_TITLE'),
        content: this.$t('INTERACTION_BASE_TIMESUP_TEXT'),
        onConfirm: this.handleTimesUpConfirm.bind(this)
      })
    },
    handleTimesUpConfirm() {
      // moves currentItem further back to answer later
      this.postponeItem({
        interactionInstanceToken: this.instanceToken,
        itemId: this.item.itemId,
      });
    },
    notifyDone() {
      const isIFrame = this.$route.query.embed === QUERY_PARAM_IFRAME;
      const parentIsDefined = !!this.$route.query.parent;
      const parentExists = !!window.parent;
      const interactionDone = isDone(this.item);

      if(interactionDone && isIFrame && parentIsDefined && parentExists) {
        const message = {
          type: "INTERACTION_COMPLETED",
          properties: []
        };
        window.parent.postMessage(message, '*');
      }
    }
  },
  mounted() {
    // send item open call when the container is being mounted, this fixes an issue where we were
    // missing item open events, for the first item in a sequence / section. This happens because
    // the item() watcher defined above, is only triggered when an item changes, but this is not
    // the case on the initial rendering of the container
    if(!this.item.opened) {
      this.openCurrentItem()
    }
    if (hasMetaData(this.item)) {
      this.getItemMetaData()
    }

    if(isDone(this.item)) {
      this.notifyDone();
    }
  }
}
</script>
<style lang="scss" scoped>
  .interaction-timer {
    position: fixed;
    top: 4.5em;
    right: -0.75em;
    z-index: 10;
    opacity: 0.9;

    @media screen and (max-width: 575px) {
      position: fixed;
      top: initial;
      right: -0.75em;
      bottom: 0.25em;
      z-index: 1020;
    }
    @media screen and (min-width: 567px) and (max-width: 768px) {
      position: absolute;
      top: 2em;
      right: 1em;
    }
    @media screen and (min-width: 768px) {
      position: absolute;
      top: 2em;
      right: 0em;
    }
    @media screen and (min-width: 1200px) {
      right: 2em;
    }
  }
</style>
